import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, LinearProgress } from '@mui/material';
import Navbar from './components/Layout/Navbar';
import Footer from './components/Layout/Footer';
import { useAuth } from './contexts/AuthContext';
import { getRoleAppearance } from './utils/roleAppearance';
import { canAccessModule, canInviteTalent, canUseAnnotator, canUseDatasetWorkspace, canUseVendorTools, isAdmin, isBuyerOperator } from './utils/accessControl';

const loadHome = () => import('./pages/Home');
const loadPersonaPage = () => import('./pages/PersonaPage');
const loadBenchmark = () => import('./pages/Benchmark');
const loadBenchmarkUseCase = () => import('./pages/BenchmarkUseCase');
const loadMarketplace = () => import('./pages/Marketplace');
const loadDatasetDetail = () => import('./pages/DatasetDetail');
const loadModelDetail = () => import('./pages/ModelDetail');
const loadLogin = () => import('./pages/Login');
const loadRegister = () => import('./pages/Register');
const loadDashboard = () => import('./pages/Dashboard');
const loadBuyerIntel = () => import('./pages/BuyerIntel');
const loadDemand = () => import('./pages/Demand');
const loadAdmin = () => import('./pages/Admin');
const loadCompareDatasets = () => import('./pages/CompareDatasets');
const loadRequestDataset = () => import('./pages/RequestDataset');
const loadCallForData = () => import('./pages/CallForData');
const loadCallForDataDetail = () => import('./pages/CallForDataDetail');
const loadMyDatasets = () => import('./pages/MyDatasets');
const loadSettings = () => import('./pages/Settings');
const loadSmartSearch = () => import('./pages/SmartSearch');
const loadAnnotator = () => import('./pages/Annotator');
const loadTasks = () => import('./pages/Tasks');
const loadTaskDetail = () => import('./pages/TaskDetail');
const loadTaskApply = () => import('./pages/TaskApply');
const loadTalent = () => import('./pages/Talent');
const loadTalentProfile = () => import('./pages/TalentProfile');
const loadMyWork = () => import('./pages/MyWork');
const loadMyTalentProfile = () => import('./pages/MyTalentProfile');

const Home = React.lazy(loadHome);
const PersonaPage = React.lazy(loadPersonaPage);
const Benchmark = React.lazy(loadBenchmark);
const BenchmarkUseCase = React.lazy(loadBenchmarkUseCase);
const Marketplace = React.lazy(loadMarketplace);
const DatasetDetail = React.lazy(loadDatasetDetail);
const ModelDetail = React.lazy(loadModelDetail);
const Login = React.lazy(loadLogin);
const Register = React.lazy(loadRegister);
const Dashboard = React.lazy(loadDashboard);
const BuyerIntel = React.lazy(loadBuyerIntel);
const Demand = React.lazy(loadDemand);
const Admin = React.lazy(loadAdmin);
const CompareDatasets = React.lazy(loadCompareDatasets);
const RequestDataset = React.lazy(loadRequestDataset);
const CallForData = React.lazy(loadCallForData);
const CallForDataDetail = React.lazy(loadCallForDataDetail);
const MyDatasets = React.lazy(loadMyDatasets);
const Settings = React.lazy(loadSettings);
const SmartSearch = React.lazy(loadSmartSearch);
const Annotator = React.lazy(loadAnnotator);
const Tasks = React.lazy(loadTasks);
const TaskDetail = React.lazy(loadTaskDetail);
const TaskApply = React.lazy(loadTaskApply);
const Talent = React.lazy(loadTalent);
const TalentProfile = React.lazy(loadTalentProfile);
const MyWork = React.lazy(loadMyWork);
const MyTalentProfile = React.lazy(loadMyTalentProfile);

const RouteFallback = () => (
  <Box
    sx={{
      minHeight: '40vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      px: 3,
    }}
  >
    <Box sx={{ width: 'min(420px, 100%)' }}>
      <LinearProgress sx={{ height: 6 }} />
    </Box>
  </Box>
);

function App() {
  const { user } = useAuth();
  const location = useLocation();
  const isLandingPage = location.pathname === '/';
  const roleAppearance = getRoleAppearance(user?.role, user?.approval?.accountType || user?.onboarding?.accountType);
  const homeElement = user ? <Navigate to="/dashboard" replace /> : <Home />;
  const loginElement = user ? <Navigate to="/dashboard" replace /> : <Login />;
  const registerElement = user ? <Navigate to="/dashboard" replace /> : <Register />;
  const requireAccount = (element) =>
    user ? (
      element
    ) : (
      <Navigate
        to="/login"
        replace
        state={{ message: 'Create an account and wait for super-admin approval to access the platform.' }}
      />
    );
  const requireCapability = (allowed, element) => allowed ? element : <Navigate to="/dashboard" replace />;
  // Super-admin-controlled module gate: requires an account AND the module enabled for this user.
  const requireModule = (moduleKey, element) =>
    canAccessModule(user, moduleKey) ? requireAccount(element) : <Navigate to="/dashboard" replace />;

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const connection = window.navigator?.connection;
    if (connection?.saveData || /2g/.test(connection?.effectiveType || '')) {
      return undefined;
    }

    const primaryLoaders = user
      ? [loadDashboard, loadMarketplace, loadBenchmark, loadSmartSearch]
      : [loadLogin, loadRegister];
    const secondaryLoaders = user
      ? [loadRequestDataset, loadCallForData, loadMyDatasets, loadSettings, loadCompareDatasets]
      : [loadLogin, loadRegister];
    const publicSecondaryLoaders = [loadHome];

    if (isAdmin(user)) {
      secondaryLoaders.push(loadAdmin);
    }
    if (canUseVendorTools(user)) {
      secondaryLoaders.push(loadBuyerIntel);
    }
    if (canUseAnnotator(user)) {
      secondaryLoaders.push(loadAnnotator);
    }

    let secondaryHandle;
    const preloadGroup = (loaders) => {
      loaders.forEach((loadPage) => {
        loadPage().catch(() => null);
      });
    };

    const preload = () => {
      preloadGroup(primaryLoaders);
      secondaryHandle = window.setTimeout(
        () => preloadGroup(user ? secondaryLoaders : publicSecondaryLoaders),
        1200
      );
    };

    if (typeof window.requestIdleCallback === 'function') {
      const handle = window.requestIdleCallback(preload, { timeout: 1500 });
      return () => {
        window.cancelIdleCallback?.(handle);
        window.clearTimeout(secondaryHandle);
      };
    }

    const handle = window.setTimeout(preload, 250);
    return () => {
      window.clearTimeout(handle);
      window.clearTimeout(secondaryHandle);
    };
  }, [user]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: roleAppearance.shellBg,
        position: 'relative',
        '&::before': roleAppearance.label
          ? {
              content: '""',
              position: 'fixed',
              inset: 0,
              pointerEvents: 'none',
              background: roleAppearance.shellGlow,
              zIndex: 0,
            }
          : undefined,
      }}
    >
      <Navbar />
      <Box component="main" sx={{ flex: 1, py: isLandingPage ? 0 : 3, px: isLandingPage ? 0 : { xs: 2, md: 5, lg: 8, xl: 12 }, position: 'relative', zIndex: 1 }}>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={homeElement} />
            <Route path="/for/:slug" element={<PersonaPage />} />
            <Route path="/benchmarks" element={requireModule('benchmarks', <Benchmark />)} />
            <Route path="/benchmarks/use-cases/:slug" element={requireModule('benchmarks', <BenchmarkUseCase />)} />
            <Route path="/marketplace" element={requireModule('marketplace', <Marketplace />)} />
            <Route path="/search" element={requireModule('search', <SmartSearch />)} />
            <Route path="/datasets/:id" element={requireModule('marketplace', <DatasetDetail />)} />
            <Route path="/compare" element={requireModule('marketplace', <CompareDatasets />)} />
            <Route path="/models/:id" element={requireModule('benchmarks', <ModelDetail />)} />
            <Route path="/login" element={loginElement} />
            <Route path="/register" element={registerElement} />
            <Route path="/request-dataset" element={requireAccount(requireCapability(isBuyerOperator(user) && canAccessModule(user, 'request-data'), <RequestDataset />))} />
            <Route path="/call-for-data" element={requireModule('call-for-data', <CallForData />)} />
            <Route path="/call-for-data/:id" element={requireModule('call-for-data', <CallForDataDetail />)} />
            {user && (
              <>
                <Route path="/dashboard" element={<Dashboard />} />
                {canUseVendorTools(user) && canAccessModule(user, 'buyer-intel') && (
                  <Route path="/buyer-intel" element={<BuyerIntel />} />
                )}
                {canUseVendorTools(user) && canAccessModule(user, 'demand') && (
                  <Route path="/demand" element={<Demand />} />
                )}
                <Route path="/my-datasets" element={requireCapability(canUseDatasetWorkspace(user) && canAccessModule(user, 'my-datasets'), <MyDatasets />)} />
                {canInviteTalent(user) && canAccessModule(user, 'talent') && (
                  <>
                    <Route path="/talent" element={<Talent user={user} />} />
                    <Route path="/talent/:id" element={<TalentProfile user={user} />} />
                  </>
                )}
                <Route path="/settings" element={<Settings />} />
                {canUseAnnotator(user) && (
                  <>
                    {canAccessModule(user, 'workbench') && <Route path="/workbench" element={<Annotator user={user} />} />}
                    <Route path="/annotator" element={<Navigate to="/workbench" replace />} />
                    {canAccessModule(user, 'tasks') && <Route path="/tasks" element={<Tasks />} />}
                    {canAccessModule(user, 'tasks') && <Route path="/tasks/:id" element={<TaskDetail />} />}
                    {canAccessModule(user, 'tasks') && <Route path="/tasks/:id/apply" element={<TaskApply user={user} />} />}
                    {canAccessModule(user, 'tasks') && <Route path="/my-work" element={<MyWork />} />}
                    <Route path="/my-profile" element={<MyTalentProfile user={user} />} />
                  </>
                )}
                {isAdmin(user) && (
                  <Route path="/admin" element={<Admin />} />
                )}
              </>
            )}
            <Route path="*" element={<Navigate to={user ? '/dashboard' : '/'} replace />} />
          </Routes>
        </Suspense>
      </Box>
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Footer />
      </Box>
    </Box>
  );
}

export default App;
